import { Text, VStack } from '@chakra-ui/react';
import { BackofficeActivitiesOrderItem } from '@diamond/shared/types';
import { numberWithSeparator } from '@diamond/shared/utils';

import { useQuantityFormat } from '../../../hooks/use-quantity-format';
import { ColumnComponentProps } from '../../../types/activity-column-types';
import {
  getActivityItemDisplay,
  hasFreeGoods,
  resolveCheckoutQuantityInput,
  shouldShowCheckoutQuantityPlaceholder,
} from '../../../utils/activity-formatters';
import {
  ActivityDetailDiffLabel,
  ActivityDetailStagePlaceholder,
} from '../../activity-detail/activity-detail-stage-qty-cell';

export function QuantityColumn({
  info,
  variant = 'buyer-aligned',
  orderStatus,
}: ColumnComponentProps<BackofficeActivitiesOrderItem, number | undefined> & {
  variant?: 'checkout' | 'buyer-aligned';
  orderStatus?: string;
}) {
  const item = info.row.original;
  const display = getActivityItemDisplay(item);
  // checkout_quantity is a nullable snapshot field; legacy PO rows fall back to confirmed_quantity.
  const checkoutQuantityOverride = hasFreeGoods(item)
    ? undefined
    : resolveCheckoutQuantityInput(
        orderStatus,
        item.checkout_quantity,
        item.quantity,
        item.confirmed_quantity
      );
  const checkoutFormat = useQuantityFormat(item, checkoutQuantityOverride);
  const buyerFormat = useQuantityFormat(
    item,
    item.confirmed_quantity ?? undefined
  );

  if (variant === 'checkout') {
    if (
      shouldShowCheckoutQuantityPlaceholder(
        orderStatus,
        item.checkout_quantity,
        item.quantity
      )
    ) {
      return <ActivityDetailStagePlaceholder />;
    }

    if (checkoutFormat.multiplier && checkoutFormat.multiplier !== 1) {
      return (
        <VStack alignItems="center">
          <Text fontSize="sm" fontWeight="400">
            {checkoutFormat.quantity}
          </Text>
          <Text color="gray.500" fontSize="sm" fontWeight="400">
            (Kelipatan: {checkoutFormat.multiplier})
          </Text>
        </VStack>
      );
    }

    return (
      <Text fontSize="sm" fontWeight="400">
        {checkoutFormat.quantity}
      </Text>
    );
  }

  if (buyerFormat.multiplier && buyerFormat.multiplier !== 1) {
    return (
      <VStack alignItems="center" spacing={0}>
        <Text fontSize="sm" fontWeight="400">
          {buyerFormat.quantity}
        </Text>
        <Text color="gray.500" fontSize="sm" fontWeight="400">
          (Kelipatan: {buyerFormat.multiplier})
        </Text>
        {display.showInitialQuantity ? (
          <ActivityDetailDiffLabel
            label="Pesanan Awal"
            value={numberWithSeparator(display.checkoutQuantity)}
            align="center"
          />
        ) : null}
      </VStack>
    );
  }

  return (
    <VStack alignItems="center" spacing={0}>
      <Text fontSize="sm" fontWeight="400">
        {buyerFormat.quantity}
      </Text>
      {display.showInitialQuantity ? (
        <ActivityDetailDiffLabel
          label="Pesanan Awal"
          value={numberWithSeparator(display.checkoutQuantity)}
          align="center"
        />
      ) : null}
    </VStack>
  );
}
